"use client";

// Phase 5 — Build Pipeline (HTML overlay) avec narration chorégraphiée :
//   1. Le code se tape caractère par caractère dans l'IDE (5s)
//   2. Une impulsion part vers la fenêtre Build via la flèche
//   3. La fenêtre Build assemble ses pixels (2s)
//   4. Une seconde impulsion part vers la fenêtre Live
//   5. Le site final se révèle section par section (2s)
//   6. Petit hold puis on reboucle
//
// Tout pilotage via une horloge globale (rAF) qui calcule la phase active
// et le progrès. Pas de keyframe CSS — tout est rendu en React pour que la
// séquence reste cohérente d'une étape à l'autre.

import { motion } from "motion/react";
import { useEffect, useRef, useState } from "react";

// ───────────────────────── timeline (seconds)

const T = {
  typeStart: 0.3,
  typeEnd: 2.8,
  arrow1Start: 2.6,
  arrow1End: 3.3,
  buildStart: 3.1,
  buildEnd: 5.0,
  arrow2Start: 4.8,
  arrow2End: 5.5,
  liveStart: 5.2,
  liveEnd: 7.4,
  hold: 8.6,
};
const LOOP = T.hold;

function useLoopClock() {
  const [t, setT] = useState(0);
  useEffect(() => {
    let raf = 0;
    const start = performance.now();
    const tick = () => {
      const elapsed = ((performance.now() - start) / 1000) % LOOP;
      setT(elapsed);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);
  return t;
}

// ───────────────────────── chrome

function TrafficLights() {
  return (
    <div className="flex items-center gap-1.5">
      <span className="size-2.5 rounded-full bg-rose-400/90" />
      <span className="size-2.5 rounded-full bg-amber-300/90" />
      <span className="size-2.5 rounded-full bg-emerald-400/90" />
    </div>
  );
}

function WindowChrome({
  tabs,
  activeTab,
  active,
  children,
  showSidebar = false,
}: {
  tabs: { name: string; dot?: boolean }[];
  activeTab: string;
  active: boolean;
  children: React.ReactNode;
  showSidebar?: boolean;
}) {
  return (
    <div
      className={`flex h-full flex-col overflow-hidden rounded-xl border bg-[#0B1220] transition-all duration-500 ${
        active
          ? "border-cyan-400/40 shadow-[0_30px_80px_-20px_rgba(34,211,238,0.45),0_0_0_1px_rgba(34,211,238,0.30)]"
          : "border-white/10 shadow-[0_30px_80px_-20px_rgba(8,12,28,0.6)]"
      }`}
    >
      <div className="flex items-center justify-between border-b border-white/5 bg-[#0F1A2E] px-3 py-2">
        <div className="flex items-center gap-1">
          {tabs.map((t) => {
            const isActive = t.name === activeTab;
            return (
              <div
                key={t.name}
                className={`flex items-center gap-1.5 rounded-md px-2.5 py-1 font-mono text-[10px] ${
                  isActive ? "bg-[#0B1220] text-zinc-100" : "text-zinc-500"
                }`}
              >
                <span className="size-1.5 rounded-sm bg-cyan-400/80" />
                <span>{t.name}</span>
                {t.dot && <span className="size-1 rounded-full bg-cyan-400" />}
              </div>
            );
          })}
        </div>
        <TrafficLights />
      </div>

      <div className="flex flex-1 overflow-hidden">
        {showSidebar && (
          <div className="w-[110px] shrink-0 border-r border-white/5 bg-[#0A1120] px-2 py-3 font-mono text-[10px]">
            <div className="px-1.5 pb-2 text-[9px] uppercase tracking-[0.16em] italic text-zinc-500">
              Explorer
            </div>
            {[
              { name: "project", folder: true },
              { name: "src", folder: true },
              { name: "components", folder: true },
              { name: "index.html", file: true, active: true },
              { name: "styles.css", file: true },
              { name: "App.jsx", file: true },
              { name: "utils.js", file: true },
            ].map((it) => (
              <div
                key={it.name}
                className={`flex items-center gap-1.5 rounded px-1.5 py-1 ${
                  it.active ? "bg-cyan-500/10 text-cyan-200" : "text-zinc-400"
                }`}
              >
                <span className="text-zinc-600">{it.folder ? "▸" : "▢"}</span>
                <span className="truncate">{it.name}</span>
              </div>
            ))}
          </div>
        )}
        <div className="flex-1 overflow-hidden">{children}</div>
      </div>
    </div>
  );
}

// ───────────────────────── stage 1: IDE typing

type Seg = { t: string; c: string };
const C = {
  v: "text-violet-400",       // keywords
  c: "text-cyan-300",         // identifiers / JSX
  s: "text-zinc-300",         // syntax / punctuation
  y: "text-amber-300",        // function names
  o: "text-orange-300",       // params
  e: "text-emerald-300",      // strings / comments
  z: "text-zinc-500",
};

const LINES: Seg[][] = [
  [{ t: "const ", c: C.v }, { t: "kessem", c: C.c }, { t: " = ", c: C.s }, { t: "magic", c: C.y }, { t: "();", c: C.s }],
  [{ t: "function ", c: C.v }, { t: "build", c: C.y }, { t: "(", c: C.s }, { t: "dream", c: C.o }, { t: ") {", c: C.s }],
  [{ t: "  return ", c: C.v }, { t: "<Studio>", c: C.c }],
  [{ t: "    {", c: C.s }, { t: "dream", c: C.o }, { t: ".pages}", c: C.s }],
  [{ t: "  </Studio>;", c: C.c }],
  [{ t: "}", c: C.s }],
  [{ t: "await ", c: C.v }, { t: "deploy", c: C.y }, { t: "(", c: C.s }, { t: "site", c: C.o }, { t: ");", c: C.s }],
  [{ t: "// ✓ shipped — 7d", c: C.e }],
];
const TOTAL_CHARS = LINES.reduce((s, l) => s + l.reduce((a, b) => a + b.t.length, 0), 0);

function visibleSegments(line: Seg[], maxChars: number): Seg[] {
  let used = 0;
  const out: Seg[] = [];
  for (const seg of line) {
    if (used >= maxChars) break;
    const remain = maxChars - used;
    if (seg.t.length <= remain) {
      out.push(seg);
      used += seg.t.length;
    } else {
      out.push({ t: seg.t.slice(0, remain), c: seg.c });
      used = maxChars;
    }
  }
  return out;
}

function IdeStage({ elapsed }: { elapsed: number }) {
  // Compute how many total chars have been typed
  let typed = 0;
  if (elapsed >= T.typeStart && elapsed <= T.typeEnd) {
    const p = (elapsed - T.typeStart) / (T.typeEnd - T.typeStart);
    typed = Math.floor(p * TOTAL_CHARS);
  } else if (elapsed > T.typeEnd) {
    typed = TOTAL_CHARS;
  }
  // Reset visually once we leave the IDE phase entirely (after live phase begins)
  if (elapsed > T.liveStart) typed = TOTAL_CHARS; // keep code shown until loop reset

  const active = elapsed < T.arrow1End;

  // Per-line visible chars
  let consumed = 0;
  const lineRenders = LINES.map((line, idx) => {
    const lineLen = line.reduce((a, b) => a + b.t.length, 0);
    const lineVisible = Math.max(0, Math.min(lineLen, typed - consumed));
    const segs = visibleSegments(line, lineVisible);
    consumed += lineLen;
    return { idx, segs, started: typed > consumed - lineLen, lineLen, lineVisible };
  });

  // Cursor position: last started line
  const lastStarted = lineRenders.findLast((l) => l.lineVisible > 0) ?? lineRenders[0];
  const cursorLine = lastStarted ? lastStarted.idx : 0;
  const cursorAtEnd = lastStarted ? lastStarted.lineVisible >= lastStarted.lineLen : false;

  return (
    <WindowChrome
      tabs={[{ name: "index.html", dot: true }, { name: "styles.css" }]}
      activeTab="index.html"
      active={active}
      showSidebar
    >
      <div className="flex h-full flex-col">
        <div className="border-b border-white/5 px-4 py-1.5 font-mono text-[9px] text-zinc-500">
          NexSite <span className="mx-1.5 text-zinc-700">›</span> index.html{" "}
          <span className="mx-1.5 text-zinc-700">›</span> …
        </div>
        <div className="flex flex-1 overflow-hidden">
          <div className="flex w-7 flex-col items-end gap-[3px] py-3 pe-1.5 font-mono text-[10px] text-zinc-700">
            {LINES.map((_, i) => (
              <span key={i} className={cursorLine === i ? "text-cyan-400" : ""}>
                {i + 1}
              </span>
            ))}
          </div>
          <div className="relative flex flex-1 flex-col gap-[3px] py-3 pe-3 font-mono text-[11px] leading-[14px]">
            {lineRenders.map(({ idx, segs }) => (
              <div key={idx} className="flex h-[14px] items-center whitespace-pre">
                {segs.map((s, i) => (
                  <span key={i} className={s.c}>
                    {s.t}
                  </span>
                ))}
                {idx === cursorLine && (
                  <span
                    className="ms-px inline-block h-[12px] w-[6px] bg-cyan-300"
                    style={{
                      animation: cursorAtEnd ? "kessem-cursor-blink 1s steps(2) infinite" : "none",
                    }}
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </WindowChrome>
  );
}

// ───────────────────────── stage 2: Build pixels

function BuildStage({ elapsed }: { elapsed: number }) {
  const COLS = 12;
  const ROWS = 8;

  // Build progress: 0 outside window, 0..1 inside.
  let progress = 0;
  if (elapsed >= T.buildStart && elapsed <= T.buildEnd) {
    progress = (elapsed - T.buildStart) / (T.buildEnd - T.buildStart);
  } else if (elapsed > T.buildEnd) progress = 1;

  // Hold during live → keep showing the assembled grid.
  if (elapsed > T.liveStart && elapsed < T.hold) progress = 1;

  const active = elapsed >= T.arrow1Start && elapsed <= T.arrow2End;

  const cells: { active: boolean; accent: boolean; phase: number }[] = [];
  for (let r = 0; r < ROWS; r++) {
    for (let c = 0; c < COLS; c++) {
      let isActive = false;
      let accent = false;
      if (r === 0) isActive = c < COLS - 2;
      else if (r >= 2 && r <= 3) {
        isActive = c >= 1 && c <= COLS - 2;
        accent = isActive;
      } else if (r >= 5 && r <= 6) {
        const block = Math.floor(c / 4);
        isActive = c % 4 < 3 && block < 3;
      } else if (r === 7) isActive = c < 3;
      const phase = (c + r * 0.6) / (COLS + ROWS);
      cells.push({ active: isActive, accent, phase });
    }
  }

  return (
    <WindowChrome
      tabs={[{ name: "preview.kessem", dot: true }]}
      activeTab="preview.kessem"
      active={active}
    >
      <div className="flex h-full flex-col">
        <div className="border-b border-white/5 px-4 py-1.5 font-mono text-[9px] text-zinc-500">
          ▸ npm run build
          <span className="ms-2 text-emerald-400/80">
            {progress >= 1 ? "ready" : progress > 0 ? "building…" : "idle"}
          </span>
        </div>
        <div className="flex flex-1 items-center justify-center p-4">
          <div
            className="grid w-full gap-[3px]"
            style={{
              gridTemplateColumns: `repeat(${COLS}, 1fr)`,
              gridTemplateRows: `repeat(${ROWS}, 1fr)`,
              aspectRatio: `${COLS}/${ROWS}`,
            }}
          >
            {cells.map((cell, i) => {
              const visible = cell.active && cell.phase <= progress;
              return (
                <div
                  key={i}
                  className={`rounded-[2px] transition-opacity duration-200 ${
                    cell.active
                      ? cell.accent
                        ? "bg-violet-500/80"
                        : "bg-cyan-500/70"
                      : "bg-white/[0.03]"
                  }`}
                  style={{
                    opacity: cell.active ? (visible ? 1 : 0) : 1,
                    transform: visible ? "scale(1)" : "scale(0.8)",
                    transition: "opacity 200ms ease, transform 200ms ease",
                  }}
                />
              );
            })}
          </div>
        </div>
      </div>
    </WindowChrome>
  );
}

// ───────────────────────── stage 3: Live site

function LiveStage({ elapsed }: { elapsed: number }) {
  // Each section reveals at its own threshold within the live window.
  const local =
    elapsed < T.liveStart ? 0 : elapsed > T.liveEnd ? 1 : (elapsed - T.liveStart) / (T.liveEnd - T.liveStart);

  const active = elapsed >= T.arrow2Start && elapsed <= T.hold;
  const reveal = (threshold: number) => (local >= threshold ? 1 : 0);

  return (
    <WindowChrome
      tabs={[{ name: "kessem.studio", dot: true }]}
      activeTab="kessem.studio"
      active={active}
    >
      <div className="flex h-full flex-col">
        <div className="flex items-center gap-2 border-b border-white/5 px-4 py-1.5">
          <span className="size-1.5 rounded-full bg-emerald-400/80" />
          <span className="font-mono text-[9px] text-zinc-500">https://kessem.studio</span>
        </div>
        <div className="flex flex-1 flex-col gap-2 p-3">
          {/* Header */}
          <div
            className="flex items-center justify-between rounded-md border border-cyan-400/30 bg-cyan-500/[0.06] px-3 py-1.5 transition-all duration-500"
            style={{ opacity: reveal(0.0), transform: `translateY(${(1 - reveal(0.0)) * -8}px)` }}
          >
            <div className="h-1.5 w-12 rounded-sm bg-cyan-400/70" />
            <div className="flex gap-1.5">
              <div className="h-1 w-6 rounded-sm bg-zinc-500/60" />
              <div className="h-1 w-6 rounded-sm bg-zinc-500/60" />
              <div className="h-1 w-6 rounded-sm bg-zinc-500/60" />
            </div>
          </div>
          {/* Hero */}
          <div
            className="relative flex flex-1 flex-col justify-center gap-2 rounded-md border border-violet-400/30 bg-gradient-to-br from-violet-500/[0.12] to-cyan-500/[0.06] px-4 transition-all duration-500"
            style={{ opacity: reveal(0.2), transform: `scale(${0.96 + reveal(0.2) * 0.04})` }}
          >
            <div className="h-2 w-3/5 rounded-sm bg-zinc-200/80" />
            <div className="h-1.5 w-2/5 rounded-sm bg-zinc-400/60" />
            <div className="mt-1 h-2 w-16 rounded-sm bg-violet-400/80" />
          </div>
          {/* Cards */}
          <div
            className="grid grid-cols-3 gap-1.5 transition-all duration-500"
            style={{ opacity: reveal(0.55), transform: `translateY(${(1 - reveal(0.55)) * 8}px)` }}
          >
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className="flex flex-col gap-1 rounded-md border border-cyan-400/25 bg-cyan-500/[0.05] p-2"
              >
                <div className="h-4 rounded-sm bg-zinc-700/50" />
                <div className="h-1 w-full rounded-sm bg-zinc-400/50" />
                <div className="h-1 w-2/3 rounded-sm bg-zinc-500/40" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </WindowChrome>
  );
}

// ───────────────────────── arrow connector

function Arrow({ active }: { active: boolean }) {
  return (
    <div className="flex w-10 shrink-0 items-center justify-center">
      <svg viewBox="0 0 40 12" fill="none" className="h-3 w-full" aria-hidden>
        <defs>
          <linearGradient id={`grad-${active ? "on" : "off"}`} x1="0" x2="1">
            <stop offset="0%" stopColor="#22D3EE" stopOpacity={active ? 0.2 : 0.15} />
            <stop offset="100%" stopColor="#22D3EE" stopOpacity={active ? 1 : 0.4} />
          </linearGradient>
        </defs>
        <path
          d="M0 6 L36 6 M30 1 L36 6 L30 11"
          stroke={`url(#grad-${active ? "on" : "off"})`}
          strokeWidth={active ? 1.6 : 1.1}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        {/* Travelling pulse when active */}
        {active && (
          <circle r="1.6" fill="#22D3EE">
            <animate attributeName="cx" from="0" to="36" dur="0.8s" repeatCount="indefinite" />
            <animate attributeName="cy" values="6;6" dur="0.8s" repeatCount="indefinite" />
          </circle>
        )}
      </svg>
    </div>
  );
}

// ───────────────────────── root

export function BuildPipeline() {
  const elapsed = useLoopClock();
  const arrow1Active = elapsed >= T.arrow1Start && elapsed <= T.arrow1End;
  const arrow2Active = elapsed >= T.arrow2Start && elapsed <= T.arrow2End;

  return (
    <motion.div
      aria-hidden
      initial={{ opacity: 1, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -6 }}
      transition={{
        y: { duration: 0.45, ease: [0.22, 1, 0.36, 1] },
        opacity: { duration: 0 },
      }}
      className="pointer-events-none fixed inset-0 hidden items-center justify-center md:flex"
    >
      <div className="flex w-full max-w-[1200px] items-stretch gap-2 px-6">
        <div className="h-[280px] w-1/3">
          <IdeStage elapsed={elapsed} />
        </div>
        <Arrow active={arrow1Active} />
        <div className="h-[280px] w-1/3">
          <BuildStage elapsed={elapsed} />
        </div>
        <Arrow active={arrow2Active} />
        <div className="h-[280px] w-1/3">
          <LiveStage elapsed={elapsed} />
        </div>
      </div>
    </motion.div>
  );
}
